import type { Invoice, InvoiceKind, PaymentMethodCard } from '@/api/types'

/**
 * Pure rules behind the Invoices page (2026-10-BILLING-IN-APP). The server decides everything that
 * matters (the kind comes from our own records, the PDF from Stripe); this only decides what to draw.
 */

export const INVOICE_KINDS: readonly { value: InvoiceKind | 'all'; label: string }[] = [
  { value: 'all', label: 'All invoices' },
  { value: 'subscription', label: 'Subscription' },
  { value: 'topup', label: 'Top-ups' },
  { value: 'recharge', label: 'Auto-recharge' },
  { value: 'other', label: 'Other' },
]

export function kindLabel(kind: string): string {
  switch (kind) {
    case 'subscription':
      return 'Subscription'
    case 'topup':
      return 'Top-up'
    case 'recharge':
      return 'Auto-recharge'
    default:
      return 'Other'
  }
}

export type StatusTone = 'default' | 'secondary' | 'outline' | 'success' | 'destructive'

export function invoiceStatus(status: string): { label: string; tone: StatusTone } {
  switch (status) {
    case 'paid':
      return { label: 'Paid', tone: 'success' }
    case 'open':
      return { label: 'Open', tone: 'destructive' }
    case 'void':
      return { label: 'Void', tone: 'secondary' }
    case 'uncollectible':
      return { label: 'Uncollectible', tone: 'destructive' }
    case 'draft':
      return { label: 'Draft', tone: 'outline' }
    default:
      return { label: status, tone: 'outline' }
  }
}

/** An open invoice with something owed: highlighted, and offers Pay now. */
export function isPayable(invoice: Pick<Invoice, 'status' | 'amount_due_cents'>): boolean {
  return invoice.status === 'open' && invoice.amount_due_cents > 0
}

/** The id `?open=<id>` names, when it is on the loaded rows (the page then scrolls and expands it). */
export function openTarget(search: string, invoices: readonly Pick<Invoice, 'id'>[]): string | null {
  const wanted = new URLSearchParams(search).get('open')
  if (wanted === null || wanted === '') return null
  return invoices.some((invoice) => invoice.id === wanted) ? wanted : null
}

export function invoiceLabel(invoice: Pick<Invoice, 'number' | 'id'>): string {
  return invoice.number ?? invoice.id
}

/** The card pre-selected in "Pay now": the default, else the first. */
export function preferredCard(cards: readonly PaymentMethodCard[]): string | undefined {
  return (cards.find((card) => card.is_default) ?? cards[0])?.id
}

/** A 402 `payment_declined` names Stripe's sentence; anything else falls through to the generic text. */
export function payErrorText(code: string | undefined, message: string): string {
  switch (code) {
    case 'payment_declined':
      return `The card was declined: ${message}`
    case 'invoice_not_open':
      return 'This invoice is no longer open. Reload the page to see its current state.'
    default:
      return message
  }
}

export function lineSummary(invoice: Pick<Invoice, 'lines'>): string {
  const first = invoice.lines[0]?.description
  if (first === undefined || first === null || first === '') return ''
  return invoice.lines.length > 1 ? `${first} and ${invoice.lines.length - 1} more` : first
}
